// API utility for fetching sponsor log and calendar data.
// Endpoints are now configurable via:
//  - options.baseUrl passed to each function
//  - window.API_BASE_URL (set in index.html)
//  - process.env.REACT_APP_API_BASE
//  - fallback default (see apiConfig.js)
// New sponsor log response shape example:
// {
//   SponsorLogs: [...], Hourly: [...], Daily: [...], Summary: [...]
// }
// We normalize SponsorLogs into a flat array compatible with existing components.
import { getApiBase, buildEndpoint } from '../config/apiConfig';

export async function fetchSponsorLog(year, tournamentId, { signal, baseUrl } = {}) {
  if (!year || !tournamentId) {
    throw new Error('Year and tournamentId are required');
  }
  const endpoint = buildEndpoint(getApiBase(baseUrl), 'api/BroadcastSponsorLog', { year, tournamentId });
  const res = await fetch(endpoint, { signal });
  if (!res.ok) {
    throw new Error(`Fetch failed: ${res.status} ${res.statusText}`);
  }
  const json = await res.json();

  // Guard for object shape
  let sponsorLogs = Array.isArray(json?.SponsorLogs) ? json.SponsorLogs : [];
  const hourly = Array.isArray(json?.Hourly) ? json.Hourly : [];
  const daily = Array.isArray(json?.Daily) ? json.Daily : [];
  const summary = Array.isArray(json?.Summary) ? json.Summary : [];

  if (json?.Content?.SponsorLogList && Array.isArray(json.Content.SponsorLogList)) {
    sponsorLogs = json.Content.SponsorLogList.map(item => ({
      ...item,
      EventYear: json.EventYear,
      EventId: json.EventId,
      MatchId: json.MatchId
    }));
  }

  // Normalize SponsorLogs entries to previous expected fields
  const normalizedSponsorLogs = sponsorLogs.map(log => {
    // Calculate duration from timestamps if EventDuration not present
    let duration = 0;
    if (typeof log.EventDuration === 'number') {
      duration = log.EventDuration;
    } else if (log.EventDuration) {
      duration = parseDurationToSeconds(log.EventDuration);
    } else if (log.StartEventTimestamp && log.EndEventTimestamp) {
      // Calculate from timestamps
      const start = new Date(log.StartEventTimestamp.$date || log.StartEventTimestamp);
      const end = new Date(log.EndEventTimestamp.$date || log.EndEventTimestamp);
      duration = Math.max(0, (end - start) / 1000); // Convert ms to seconds
    }

    const eventTemplate = log.EventTemplate || '';
    const eventTemplateLower = eventTemplate.toLowerCase();
    let sponsor = log.Sponsor || '';
    let graphicType = log.GraphicType || log.ContentId || '';
    
    if (eventTemplateLower === 'pif') {
      sponsor = 'PIF';
      graphicType = graphicType || 'PIF Logo';
    } else if (eventTemplateLower === 'lexus') {
      sponsor = 'Lexus';
      graphicType = graphicType || 'Lexus Logo';
    } else if (eventTemplateLower === 'rolex') {
      sponsor = 'Rolex';
      graphicType = graphicType || 'Rolex';
    } else if (eventTemplateLower === 'tournamentsponsor') {
      sponsor = 'TournamentSponsor';
      graphicType = graphicType || 'Tournament Logo';
    }

    return {
      Tournament: log.EventTitle || '',
      Date: log.Date || null,
      Year: String(log.EventYear || year),
      EventId: log.EventId || null,
      'Court Name': log.CourtName || '',
      'Graphic Type': graphicType,
      'Stats Displayed': log.StatsDisplayed || '',
      Sponsor: sponsor,
      Duration_Seconds: Math.max(0, duration), // Ensure non-negative duration
      StartTimestamp: log.StartEventTimestamp || null,
      EndTimestamp: log.EndEventTimestamp || null,
      IsOnAir: log.IsOnAir !== undefined ? log.IsOnAir : false,
      IsVisible: log.IsVisible !== undefined ? log.IsVisible : false,
      EventTemplate: eventTemplate,
      ContentId: log.ContentId || '',
      Raw: log 
    };
  });

  return {
    raw: json,
    sponsorLogs: normalizedSponsorLogs,
    hourly,
    daily,
    summary,
    endpoint
  };
}

// Fetch all sponsor logs for a given year (all tournaments combined).
export async function fetchSponsorLogsByYear(year, { signal, baseUrl } = {}) {
  if (!year) {
    throw new Error('Year is required');
  }
    const endpoint = buildEndpoint(getApiBase(baseUrl), 'api/BroadcastSponsorLogsByYear', { year });
  
  const res = await fetch(endpoint, { signal });
  if (!res.ok) {
    throw new Error(`Fetch failed: ${res.status} ${res.statusText}`);
  }
  const json = await res.json();

  // Guard for object shape (expect same structure as fetchSponsorLog)
  let sponsorLogs = Array.isArray(json?.SponsorLogs) ? json.SponsorLogs : [];
  const hourly = Array.isArray(json?.Hourly) ? json.Hourly : [];
  const daily = Array.isArray(json?.Daily) ? json.Daily : [];
  const summary = Array.isArray(json?.Summary) ? json.Summary : [];

  if (json?.Content?.SponsorLogList && Array.isArray(json.Content.SponsorLogList)) {
    sponsorLogs = json.Content.SponsorLogList.map(item => ({
      ...item,
      EventYear: json.EventYear,
      EventId: json.EventId,
      MatchId: json.MatchId
    }));
  }

  // Normalize SponsorLogs entries to previous expected fields
  const normalizedSponsorLogs = sponsorLogs.map(log => {
    
    let duration = 0;
    if (typeof log.EventDuration === 'number') {
      duration = log.EventDuration;
    } else if (log.EventDuration) {
      duration = parseDurationToSeconds(log.EventDuration);
    } else if (log.StartEventTimestamp && log.EndEventTimestamp) {
      
      const start = new Date(log.StartEventTimestamp.$date || log.StartEventTimestamp);
      const end = new Date(log.EndEventTimestamp.$date || log.EndEventTimestamp);
      duration = Math.max(0, (end - start) / 1000); // Convert ms to seconds
    }

    
    const eventTemplate = log.EventTemplate || '';
    const eventTemplateLower = eventTemplate.toLowerCase();
    let sponsor = log.Sponsor || '';
    let graphicType = log.GraphicType || log.ContentId || '';
    
    if (eventTemplateLower === 'pif') {
      sponsor = 'PIF';
      graphicType = graphicType || 'PIF Logo';
    } else if (eventTemplateLower === 'lexus') {
      sponsor = 'Lexus';
      graphicType = graphicType || 'Lexus Logo';
    } else if (eventTemplateLower === 'rolex') {
      sponsor = 'Rolex';
      graphicType = graphicType || 'Rolex';
    } else if (eventTemplateLower === 'tournamentsponsor') {
      sponsor = 'TournamentSponsor';
      graphicType = graphicType || 'Tournament Logo';
    }

    return {
      Tournament: log.EventTitle || '',
      Date: log.Date || null,
      Year: String(log.EventYear || year),
      EventId: log.EventId || null,
      'Court Name': log.CourtName || '',
      'Graphic Type': graphicType,
      'Stats Displayed': log.StatsDisplayed || '',
      Sponsor: sponsor,
      Duration_Seconds: Math.max(0, duration), // Ensure non-negative duration
      StartTimestamp: log.StartEventTimestamp || null,
      EndTimestamp: log.EndEventTimestamp || null,
      IsOnAir: log.IsOnAir !== undefined ? log.IsOnAir : false,
      IsVisible: log.IsVisible !== undefined ? log.IsVisible : false,
      EventTemplate: eventTemplate,
      ContentId: log.ContentId || '',
      Raw: log 
    };
  });

  return {
    raw: json,
    sponsorLogs: normalizedSponsorLogs,
    hourly,
    daily,
    summary,
    endpoint
  };
}

// Fetch calendar tournaments for a given year and return a simplified unique list.
export async function fetchCalendar(year, { signal, baseUrl } = {}) {
  if (!year) throw new Error('Year is required');
  const endpoint = buildEndpoint(getApiBase(baseUrl), 'api/Calendar', { year });
  const res = await fetch(endpoint, { signal });
  if (!res.ok) {
    throw new Error(`Calendar fetch failed: ${res.status} ${res.statusText}`);
  }
  const json = await res.json();
  const instances = Array.isArray(json?.TournamentInstances) ? json.TournamentInstances : [];
  // Filter strictly by requested year (API may return other years) and dedupe by ATPId
  const seen = new Set();
  const tournaments = [];
  for (const inst of instances) {
    if (String(inst.Year) !== String(year)) continue;
    const id = inst.ATPId || inst.wCompCompetitionInstanceID;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    tournaments.push({
      id: String(id),
      atpId: inst.ATPId ? String(inst.ATPId) : null,
      wCompCompetitionInstanceID: inst.wCompCompetitionInstanceID ? String(inst.wCompCompetitionInstanceID) : null,
      instanceName: inst.InstanceName || '',
      tournamentName: inst.TournamentName || inst.InstanceName || '',
      city: inst.City || '',
      country: inst.HostCountry?.CountryShortName || inst.HostCountry?.CountryName || '',
      surface: inst.Surface || '',
      startDate: inst.StartDate || null,
      endDate: inst.EndDate || null
    });
  }
  // Sort alphabetically by instanceName
  tournaments.sort((a, b) => a.instanceName.localeCompare(b.instanceName));
  return { tournaments, endpoint };
}

// Parses a duration string like HH:MM:SS or MM:SS to seconds.
function parseDurationToSeconds(value) {
  if (value == null) return 0;
  if (typeof value === 'number') return Math.max(0, value); // Ensure non-negative
  const str = String(value).trim();
  const parts = str.split(':').map(p => parseInt(p, 10));
  if (parts.some(isNaN)) return 0;
  // Support HH:MM:SS or MM:SS
  if (parts.length === 3) {
    const [h, m, s] = parts;
    return Math.max(0, h * 3600 + m * 60 + s); // Ensure non-negative
  } else if (parts.length === 2) {
    const [m, s] = parts;
    return Math.max(0, m * 60 + s); // Ensure non-negative
  }
  return 0;
}
